import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, Link } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Browse from "@/pages/Browse";
import ProductDetail from "@/pages/ProductDetail";
import Auth from "@/pages/Auth";
import Saved from "@/pages/Saved";
import Sell from "@/pages/Sell";
import Dashboard from "@/pages/Dashboard";
import Cart from "@/pages/Cart";
import Brands from "@/pages/Brands";
import Orders from "@/pages/Orders";
import Pricing from "@/pages/Pricing";
import ProofGuide from "@/pages/ProofGuide";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refunds from "@/pages/Refunds";
import LaunchChecklist from "@/pages/LaunchChecklist";
import Wallet from "@/pages/Wallet";
import Archives from "@/pages/Archives";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Admin from "@/pages/Admin";
import Seller from "@/pages/Seller";
import PrivateCollection from "@/pages/PrivateCollection";
import MySizes from "@/pages/MySizes";
import Trades from "@/pages/Trades";
import Trade from "@/pages/Trade";
import VerifyEmail from "@/pages/VerifyEmail";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Account from "@/pages/Account";
import "@/index.css";
import "@/App.css";

const qc = new QueryClient({ defaultOptions: { queries: { staleTime: 30_000, refetchOnWindowFocus: false } } });

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-16 text-center label-eyebrow">Loading…</div>;
  if (user === false) return <Navigate to="/auth" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="p-16 text-center label-eyebrow">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function NotFound() {
  return (
    <div data-testid="not-found" className="max-w-[600px] mx-auto px-6 py-28 text-center">
      <div className="label-eyebrow">Error 404</div>
      <h1 className="font-display text-6xl tracking-tighter mt-3">Lost the thread.</h1>
      <p className="mt-4 text-neutral-500">We couldn't find that page. It may have sold, moved, or never existed.</p>
      <div className="mt-8 flex gap-3 justify-center">
        <Link to="/" className="btn-primary">Back home</Link>
        <Link to="/browse" className="btn-outline">Browse the marketplace</Link>
      </div>
    </div>
  );
}

function AppShell() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/browse" element={<Browse />} />
        <Route path="/trade" element={<Trade />} />
        <Route path="/brands" element={<Brands />} />
        <Route path="/archives" element={<Archives />} />
        <Route path="/proof-guide" element={<ProofGuide />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/refunds" element={<Refunds />} />
        <Route path="/launch-checklist" element={<LaunchChecklist />} />
        <Route path="/orders" element={<Protected><Orders /></Protected>} />
        <Route path="/product/:id" element={<ProductDetail />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/saved" element={<Protected><Saved /></Protected>} />
        <Route path="/sell" element={<Protected><Sell /></Protected>} />
        <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
        <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
        <Route path="/cart" element={<Protected><Cart /></Protected>} />
        <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
        <Route path="/seller/:id" element={<Seller />} />
        <Route path="/seller/:id/private" element={<Protected><PrivateCollection /></Protected>} />
        <Route path="/my-sizes" element={<MySizes />} />
        <Route path="/account" element={<Protected><Account /></Protected>} />
        <Route path="/trades" element={<Protected><Trades /></Protected>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Layout>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <QueryClientProvider client={qc}>
      <AuthProvider>
        <BrowserRouter>
          <AppShell />
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
